import React, { useState, useEffect } from "react";
import { FaGithub, FaLinkedin, FaDownload, FaCode, FaTerminal, FaBrain, FaServer, FaDatabase, FaCloud } from "react-icons/fa";
import Particles from "react-tsparticles";
import { loadSlim } from "tsparticles-slim";
import "./Body.css";

function Body() {
  const [text, setText] = useState("");
  const [index, setIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [skills] = useState([
    { icon: FaCode, text: "Full Stack Dev", color: "#4ec9b0" },
    { icon: FaBrain, text: "AI/ML Engineer", color: "#9cdcfe" },
    { icon: FaServer, text: "Backend Dev", color: "#ce9178" },
    { icon: FaDatabase, text: "System Design", color: "#569cd6" },
    { icon: FaCloud, text: "DevOps", color: "#dcdcaa" },
  ]);
  const [currentSkill, setCurrentSkill] = useState(0);
  const [currentDirectory] = useState("~/portfolio");

  const fullText = "Hey there, I'm Vedant!";
  const typingSpeed = 150;
  const deletingSpeed = 75;
  const pauseBeforeDelete = 2500;
  const pauseBeforeRetype = 1500;

  useEffect(() => {
    const skillInterval = setInterval(() => {
      setCurrentSkill((prev) => (prev + 1) % skills.length);
    }, 2000);

    return () => clearInterval(skillInterval);
  }, [skills.length]);

  useEffect(() => {
    if (!isDeleting && index < fullText.length) {
      const timeout = setTimeout(() => {
        setText((prev) => fullText.substring(0, prev.length + 1));
        setIndex(index + 1);
      }, typingSpeed);
      return () => clearTimeout(timeout);
    } else if (isDeleting && index > 0) {
      const timeout = setTimeout(() => {
        setText((prev) => fullText.substring(0, prev.length - 1));
        setIndex(index - 1);
      }, deletingSpeed);
      return () => clearTimeout(timeout);
    } else if (!isDeleting && index === fullText.length) {
      const timeout = setTimeout(() => {
        setIsDeleting(true);
      }, pauseBeforeDelete);
      return () => clearTimeout(timeout);
    } else if (isDeleting && index === 0) {
      const timeout = setTimeout(() => {
        setIsDeleting(false);
      }, pauseBeforeRetype);
      return () => clearTimeout(timeout);
    }
  }, [index, isDeleting, fullText]);

  const particlesInit = async (engine) => {
    await loadSlim(engine);
  };

  return (
    <section id="home" className="body-section">
      <Particles
        id="tsparticles"
        init={particlesInit}
        options={{
          fullScreen: { enable: false },
          background: { color: "transparent" },
          fpsLimit: 120,
          particles: {
            color: { value: "#4ec9b0" },
            links: {
              color: "#4ec9b0",
              distance: 150,
              enable: true,
              opacity: 0.3,
              width: 1,
            },
            number: { value: 80 },
            shape: { type: "circle" },
            opacity: { value: 0.5 },
            size: { value: 2 },
            move: {
              enable: true,
              speed: 0.8,
              direction: "none",
              random: false,
              straight: false,
              outModes: { default: "bounce" },
            },
          },
          interactivity: {
            events: {
              onHover: {
                enable: true,
                mode: "grab",
              },
            },
            modes: {
              grab: {
                distance: 140,
                links: { opacity: 0.5 },
              },
            },
          },
        }}
      />

      <div className="terminal-window">
        <div className="terminal-bar">
          <div className="terminal-buttons">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <div className="terminal-title">
            <FaTerminal /> portfolio.sh — {currentDirectory}
          </div>
        </div>

        <div className="terminal-content">
          <div className="command-line">
            <span className="prompt">$</span>
            <span className="directory">{currentDirectory}</span>
            <span className="command">whoami</span>
          </div>

          <div className="profile-container">
            <div className="profile-background"></div>
            <img src="pfp.png" alt="Profile" className="profile-image" />
          </div>

          <div className="command-line">
            <span className="prompt">$</span>
            <span className="directory">{currentDirectory}</span>
            <span className="command">echo $NAME</span>
          </div>
          
          <div className="intro">
            <div className="typing-container">
              <h1>{text}<span className="cursor"></span></h1>
            </div>
          </div>

          <div className="command-line">
            <span className="prompt">$</span>
            <span className="directory">{currentDirectory}</span>
            <span className="command">get-current-role</span>
          </div>
          
          <div className="skill-badge" style={{ backgroundColor: skills[currentSkill].color }}>
            {React.createElement(skills[currentSkill].icon, { className: "skill-icon" })}
            <span>{skills[currentSkill].text}</span>
          </div>

          <div className="command-line">
            <span className="prompt">$</span>
            <span className="directory">{currentDirectory}</span>
            <span className="command">cat about.txt</span>
          </div>
          
          <div className="output-block">
            <p>
              I am a Computer Science undergraduate specializing in Full Stack Development and AI/ML Engineering. 
              With extensive experience in building scalable web applications and AI solutions, I also work on 
              system design, backend architecture, and DevOps practices. My diverse skill set allows me to tackle 
              complex technical challenges across the software development spectrum, from designing efficient 
              microservices to implementing machine learning models.
            </p>
          </div>

          <div className="command-line">
            <span className="prompt">~/portfolio</span>
            <span className="command">ls -la ./social-links/</span>
          </div>
          
          <div className="terminal-links">
            <div className="terminal-links-row">
              <a 
                href="https://github.com/vm5"
                target="_blank"
                rel="noopener noreferrer"
                className="terminal-link-btn"
              >
                <FaGithub /> GitHub
              </a>
              <a 
                href="https://www.linkedin.com/in/vedant-marathe"
                target="_blank"
                rel="noopener noreferrer"
                className="terminal-link-btn"
              >
                <FaLinkedin /> LinkedIn
              </a>
            </div>
            <a 
              href="https://docs.google.com/presentation/d/1LWrA0eNbSVLOaIjB0L-cYvffQbCLpssG/edit?usp=sharing&ouid=104625932641497391387&rtpof=true&sd=true"
              target="_blank"
              rel="noopener noreferrer"
              className="terminal-link-btn download-btn"
            >
              <FaDownload /> Download Resume
            </a>
          </div>

          <div className="command-line">
            <span className="prompt">$</span>
            <span className="directory">{currentDirectory}</span>
            <span className="cursor">|</span>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Body;
